import { useEffect, useRef, useState } from "react";

import { tabInBackground } from "src/features/notifications/visibility";
import { setConversationStatus } from "src/helpers/tabFavicon";
import { ConversationStatus } from "src/views/assistant/components/conversationStatus";

const ACK_EVENTS = ["pointerdown", "keydown"] as const;

/**
 * Mirrors the conversation state onto the browser-tab favicon, and returns the
 * status the tab is actually reporting so the header chip can show the same
 * thing.
 *
 * "complete" is an unseen-change marker, so it only survives while the user is
 * looking elsewhere: if the tab is already visible and focused when the turn
 * finishes it is never reported — the user watched it finish, so there is
 * nothing unseen to advertise — and otherwise the first sign they looked (the tab
 * becoming visible or focused, or a click or keypress in it) clears it. Error
 * stays until the status itself changes. On unmount it clears its status, so
 * the tab falls back to the unread dot or the page's own icon.
 */
const useConversationTabStatus = (
  status: ConversationStatus,
): ConversationStatus => {
  const owner = useRef(
    `conversation-status-${Math.random().toString(36).slice(2)}`,
  ).current;

  // A turn that lands in a focused tab was watched as it landed, so it is
  // never reported. Deciding that here rather than only in the effect below
  // matters: a passive effect runs after paint, so the chip flashed a green
  // "Response complete" for one frame on every turn finished in the open tab.
  const reportable = (next: ConversationStatus) =>
    next === "complete" && !tabInBackground() ? "ready" : next;

  // What the tab reports, which is the input status until it is acknowledged.
  const [reported, setReported] = useState(() => reportable(status));
  // Adjusting state during render, so a status that changes back into
  // "complete" is reported again immediately instead of one render late.
  const [lastInput, setLastInput] = useState(status);
  if (lastInput !== status) {
    setLastInput(status);
    setReported(reportable(status));
  }

  useEffect(() => {
    setConversationStatus(owner, status);
    if (status !== "complete") return undefined;

    const clear = () => {
      setReported("ready");
      setConversationStatus(owner, "ready");
    };

    // Already on screen when the turn landed. Waiting for a click instead
    // would leave the disc up: switching away fires `visibilitychange` once
    // the tab is already hidden, where `acknowledge` bails on
    // `tabInBackground()`.
    if (!tabInBackground()) {
      clear();
      return undefined;
    }

    let acknowledged = false;
    const acknowledge = () => {
      if (acknowledged || tabInBackground()) return;
      acknowledged = true;
      clear();
    };
    window.addEventListener("focus", acknowledge);
    document.addEventListener("visibilitychange", acknowledge);
    ACK_EVENTS.forEach((event) =>
      document.addEventListener(event, acknowledge),
    );
    return () => {
      window.removeEventListener("focus", acknowledge);
      document.removeEventListener("visibilitychange", acknowledge);
      ACK_EVENTS.forEach((event) =>
        document.removeEventListener(event, acknowledge),
      );
    };
  }, [owner, status]);

  useEffect(() => () => setConversationStatus(owner, null), [owner]);

  return reported;
};

export default useConversationTabStatus;
